import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from "firebase/auth";
import { auth, db, rtdb } from "../../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { ref, set, serverTimestamp } from "firebase/database";
import { generateKeyPair, exportKey, encryptPrivateKey } from "../../utils/crypto";

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMsg("");

    // Strict Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address (e.g., user@domain.com).");
      return;
    }


    // Strict Password Validation
    // > 6 chars (so 7+), 1 letter, 1 number, 1 special char
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{7,}$/;
    if (!passwordRegex.test(password)) {
      setError("Password must be >6 chars and include letters, numbers, and special characters (@$!%*#?&).");
      return;
    }

    try {
      const lowerUsername = username.toLowerCase();
      // Check secure usernames collection using getDoc (allowed)
      // query() is a list operation and is blocked by rules.
      // Actually, since we want to check existence, getDoc is better and cheaper
      const checkDoc = await getDoc(doc(db, "usernames", lowerUsername));

      if (checkDoc.exists()) {
        setError("Username taken.");
        return;
      }

      const res = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(res.user, { displayName: lowerUsername });

      // Send Email Verification
      await sendEmailVerification(res.user);
      setInfoMsg("Account created! A verification email has been sent to your inbox.");

      const keyPair = await generateKeyPair();
      const publicKeyJwk = await exportKey(keyPair.publicKey);
      const privateKeyJwk = await exportKey(keyPair.privateKey);

      // Encrypt private key with password
      const encryptedPrivateKeyData = await encryptPrivateKey(privateKeyJwk, password);

      localStorage.setItem(`privKey_${res.user.uid}`, privateKeyJwk);

      // 1. Create Public User Profile
      await setDoc(doc(db, "users", res.user.uid), {
        uid: res.user.uid,
        username: lowerUsername,
        email,
        emailVerified: false,
        photoURL: res.user.photoURL || "",
        publicKey: publicKeyJwk,
        // NO private key here anymore
        createdAt: serverTimestamp(),
      });

      // 2. Store Encrypted Private Key in Verified-Only Sub-collection
      // This path 'users/{uid}/private_data/keys' will be secured by rules
      await setDoc(doc(db, "users", res.user.uid, "private_data", "keys"), {
        encryptedPrivateKey: encryptedPrivateKeyData
      });

      // 3. Create Username Lookup Doc (Publicly accessible for login)
      await setDoc(doc(db, "usernames", lowerUsername), {
        email: email,
        uid: res.user.uid
      });

      await set(ref(rtdb, 'status/' + res.user.uid), {
        state: 'online',
        last_changed: serverTimestamp(),
      });

    } catch (err) {
      console.error("Signup error:", err);
      switch (err.code) {
        case "auth/email-already-in-use": setError("Email already used."); break;
        case "auth/weak-password": setError("Weak password/must be at least 6 characters."); break;
        case "auth/invalid-email": setError("Invalid email."); break;
        default: setError("Signup failed.");
      }
    }
  };

  return (
    <div className="p-8 w-full max-w-md mx-auto">
      <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600 transition-colors mb-6 group">
        <svg className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Create Account</h2>
        <p className="text-gray-500">Join the community today</p>
      </div>

      {infoMsg && (
        <div className="bg-green-50 text-green-700 text-sm py-3 px-4 rounded-lg mb-6 border border-green-200 animate-fade-in">
          {infoMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg animate-fade-in shadow-sm relative">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Signup Failed</h3>
              <p className="text-sm text-red-700 mt-1 leading-snug">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSignUp} className="space-y-4 text-left">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">Username</label>
          <input
            type="text"
            required
            className="w-full bg-white px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-gray-800 placeholder-gray-400 shadow-sm"
            placeholder="Choose a username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">Email</label>
          <input
            type="email"
            required
            className="w-full bg-white px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-gray-800 placeholder-gray-400 shadow-sm"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">Password</label>
          <input
            type="password"
            required
            className="w-full bg-white px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-gray-800 placeholder-gray-400 shadow-sm"
            placeholder="Create a strong password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className="w-full bg-green-600 text-white font-bold py-3.5 rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 transition-all shadow-md transform active:scale-[0.98] mt-2"
        >
          SIGN UP
        </button>

        <p className="text-xs text-center text-gray-500 mt-6 pt-4 border-t border-gray-100 pb-2">
          By signing up, you agree to our <Link to="/terms-of-service" className="text-green-600 hover:text-green-800 font-medium">Terms</Link> and <Link to="/privacy-policy" className="text-green-600 hover:text-green-800 font-medium">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
